import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { styled, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import MuiDrawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import { getResidentDetails, selectResidentDetails } from './payments/sagas/residentpaymentsSlice';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import MenuIcon from '@mui/icons-material/Menu';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Route, Switch, useRouteMatch, Link as RouterLink, useHistory, Redirect } from 'react-router-dom';
import { ListItem, ListItemIcon, ListItemText, Drawer, Grid } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import Payments from './payments/Payments';
import Copyright from '../shared/util/Copyright';
import { useDispatch } from 'react-redux';
import { clearCredentials, getUserDetails, selectAuthState, selectCurrentUser } from '../shared/sagas/auth/authSlice';
import PaymentsIcon from '../icons/paymentIcon.svg';
// import MessagesIcon from '../icons/messageIcon.svg';
import MaintenanceIcon from '../icons/maintenanceIcon.svg';
// import PromotionsIcon from '../icons/promotionsIcon.svg';
import DarkProfileSettingsIcon from '../icons/DarkProfileSettingsIcon.svg';
import ProfileSettings from '../shared/profileSettings/profileSettings';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import WhiteInosio from '../icons/whiteinosio.svg';
// import ComingSoon from '../shared/ComingSoon';
import { UIContextEnum, defaultUIContext } from '../pmui/residents/MakeAdhocOtp';
import ProfileWidget from '../shared/login/ProfileWidget';
import Maintenance from './maintenance/Maintenance';
import Applications from './applications/Applications';
import ApplicationsIcon from '../icons/ResApplicationsIcon.svg';
import { mdTheme, resUIuseStyles } from './styles/resUI';
import { getPropertyGroupDetails, selectPropertyGroupDetailsState } from '../shared/sagas/propertyGroups/propertyGroupsSlice';
import HideOnScroll from '../shared/HideOnScroll';
import { Role } from '@inosio/payments-sdk';

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

interface LeftLinkProps {
  Text: string;
  Icon: string;
  path: string;
  selected?: boolean;
}

const drawerWidth: number = 290;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DesktopDrawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  '& .MuiDrawer-paper': {
    position: 'relative',
    whiteSpace: 'nowrap',
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: 'border-box',
    ...(!open && {
      // overflowX: 'hidden',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: theme.spacing(0),
      [theme.breakpoints.up('sm')]: {
        width: theme.spacing(0),
      },
    }),
  },
}));

export default function ResUI() {
  const classes = resUIuseStyles();
  const [open, setOpen] = React.useState(true);
  const toggleDrawer = () => {
    setOpen(!open);
  };
  let { path, url } = useRouteMatch();
  const matchesMobile = useMediaQuery('(max-width:600px)');
  const isOnMobileScreen = useMediaQuery('(max-width:420px)');
  const matchesIpad = useMediaQuery('(max-width:1024px)');
  const history = useHistory();
  const dispatch = useDispatch();
  const [deviceWidth, changeDeviceSize] = React.useState(window.innerWidth);
  const [scrollTarget, setScrollTarget] = React.useState<Node | undefined>(undefined); //which component's scroll to listen to in order to hide the AppBar on scroll down
  const { propertyGroupDetails: PropertyGroupDetails } = useSelector(selectPropertyGroupDetailsState);

  const handleLogout = (event: React.MouseEvent<HTMLElement>) => {
    dispatch(clearCredentials());
    history.push('/login');
  };

  const currentUser = useSelector(selectCurrentUser);
  useEffect(() => {
    if (!currentUser?.accessToken) {
      history.push('/login');
    }
    dispatch(getUserDetails());
    const resizeW = () => changeDeviceSize(window.innerWidth);
    window.addEventListener('resize', resizeW);
    return () => window.removeEventListener('resize', resizeW);
  }, [currentUser, history, deviceWidth]);

  const residentsDetails = useSelector(selectResidentDetails);

  useEffect(() => {
    if (residentsDetails?.Lease?.PropertyGroupUuid) {
      dispatch({
        type: getPropertyGroupDetails.type,
        payload: { propertyGroupUuid: residentsDetails?.Lease?.PropertyGroupUuid, productTypeId: [1, 3] },
      });
    }
  }, [dispatch, residentsDetails?.Lease?.PropertyGroupUuid]);

  const { userDetails, ipaUser, isUpdateUserDetailsSuccess } = useSelector(selectAuthState);

  useEffect(() => {
    if (isUpdateUserDetailsSuccess) {
      //only if it's a resident
      if (ipaUser?.currentProfile?.role === Role.Resident) {
        dispatch(getResidentDetails());
      }
    }
  }, [dispatch, isUpdateUserDetailsSuccess]);

  const currentprofile = userDetails?.Profiles?.find((prof: any) => prof.Uuid === ipaUser?.currentProfile.uuid);

  const [linksOnLeft, setLinksOnLeft] = React.useState<LeftLinkProps[]>([]);
  useEffect(() => {
    if (ipaUser?.currentProfile.role === Role.Applicant) {
      setLinksOnLeft([
        {
          Text: 'Applications',
          Icon: ApplicationsIcon,
          path: `${url}/applications`,
          selected: true,
        },
        {
          Text: 'Profile Settings',
          Icon: DarkProfileSettingsIcon,
          path: `${url}/profilesettings`,
        },
      ]);
    } else {
      setLinksOnLeft([
        {
          Text: 'Payments',
          Icon: PaymentsIcon,
          path: `${url}/payments`,
          selected: true,
        },
        ...(PropertyGroupDetails.MaintenanceEnabled
          ? [
              {
                Text: 'Maintenance',
                Icon: MaintenanceIcon,
                path: `${url}/maintenance`,
              },
            ]
          : []),
        {
          Text: 'Profile Settings',
          Icon: DarkProfileSettingsIcon,
          path: `${url}/profilesettings`,
        },
      ]);
    }
  }, [ipaUser, PropertyGroupDetails]);

  type Anchor = 'left';
  const [state, setState] = React.useState({
    left: false,
  });
  const toggleMobileDrawer = (anchor: Anchor, open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (event.type === 'keydown' && ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')) {
      return;
    }

    setState({ ...state, [anchor]: open });
  };
  const pathName = window.location.pathname;

  const mainRef = React.useRef(null);

  const list = (anchor: Anchor) => (
    <Box sx={{ width: 290 }} role="presentation" onClick={toggleMobileDrawer(anchor, false)} onKeyDown={toggleMobileDrawer(anchor, false)} style={{ zIndex: 999 }}>
      <div className={classes.menuContainer}>
        <List>
          {linksOnLeft.map((link) => (
            <ListItem button key={link.Text} selected={link.path === pathName}>
              <RouterLink to={link.path} id="navLink" className={link.path === pathName ? classes.activeListItem : classes.listItem}>
                <IconButton>
                  <img
                    src={link.Icon}
                    style={{
                      width: link.path === pathName ? '35px' : '25px',
                      height: link.path === pathName ? '29px' : '25px',
                    }}
                    alt="action"
                  />
                </IconButton>
                {link.Text}
              </RouterLink>
            </ListItem>
          ))}
        </List>
      </div>
    </Box>
  );

  const getTitleText = () => {
    let text = '';

    if (pathName.slice(7, pathName.length) === 'profilesettings') {
      text = 'Profile Settings';
    } else if (pathName.slice(7, 19) === 'applications') {
      text = 'Application';
    } else if (pathName.slice(7, 15) === 'payments') {
      text = 'Payments';
    } else if (pathName.slice(7, 18) === 'maintenance') {
      text = 'Maintenance';
    } else text = 'Inosio';

    return text;
  };

  return (
    <ThemeProvider theme={mdTheme}>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <HideOnScroll scrollTarget={scrollTarget}>
          <AppBar open={open} style={{ background: 'transparent', boxShadow: 'none' }}>
            {!(matchesMobile && state['left']) ? (
              <Toolbar
                sx={{
                  pr: '24px',
                  height: '70px',
                }}
              >
                {matchesIpad ? (
                  <IconButton
                    edge="start"
                    sx={{
                      marginLeft: '-300px',
                      ...(state['left'] && { display: 'none' }),

                      height: '70px',
                      borderRadius: '0px',
                    }}
                    onClick={toggleMobileDrawer('left', true)}
                  >
                    <MenuIcon sx={{ color: '#000', cursor: 'pointer' }}>{'left'}</MenuIcon>
                  </IconButton>
                ) : (
                  ''
                )}
                {!isOnMobileScreen ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                      component="h1"
                      variant="h6"
                      noWrap
                      sx={{ flexGrow: 1 }}
                      className={classes.title}
                      id="mainTitle"
                      style={{
                        marginLeft: '0px',
                        ...(state['left'] && { display: 'none' }),
                        height: '70px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'start',
                        justifyContent: 'center',
                        // width: "400px"//TODO: temporary for mobile due to css issue
                      }}
                    >
                      {getTitleText()}
                    </Typography>

                    <Typography
                      sx={{
                        fontFamily: 'Open Sans',
                        fontStyle: 'normal',
                        fontWeight: 700,
                        fontSize: '16px',
                        lineHeight: '22px',
                        color: '#14142B',
                        marginLeft: '11px',
                        marginTop: '7px',
                      }}
                    >
                      {currentprofile?.DisplayName}
                    </Typography>
                  </Box>
                ) : (
                  ''
                )}
              </Toolbar>
            ) : (
              <></>
            )}
            <Grid sx={{ position: 'absolute', top: 0, right: 0, padding: 2 }}>{!(matchesMobile && state['left']) ? <ProfileWidget /> : <></>}</Grid>
          </AppBar>
        </HideOnScroll>

        {!matchesIpad ? (
          <DesktopDrawer variant="permanent" open={open} onClose={toggleDrawer} anchor="left">
            <div className={classes.LogoContainer}>
              <IconButton>
                <img
                  src={matchesMobile ? WhiteInosio : WhiteInosio}
                  style={{
                    width: matchesMobile ? '58px' : '74px',
                    height: matchesMobile ? '58px' : '74px',
                  }}
                  alt="action"
                />
              </IconButton>
              <Typography id="logoName">{residentsDetails?.Uuid && `${residentsDetails?.Lease?.PropertyName.slice(0, 10)}...`}</Typography>
              {matchesIpad ? (
                <Toolbar
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    px: [1],
                  }}
                >
                  <IconButton onClick={toggleDrawer}>
                    <ChevronLeftIcon />
                  </IconButton>
                </Toolbar>
              ) : (
                ''
              )}
            </div>
            <div className={classes.menuContainer}>
              <List>
                {linksOnLeft.map((link) => (
                  <ListItem button key={link.Text} selected={link.path === pathName}>
                    <RouterLink to={link.path} id="navLink" className={link.path === pathName ? classes.activeListItem : classes.listItem}>
                      <IconButton>
                        <img
                          src={link.Icon}
                          style={{
                            width: link.path === pathName ? '35px' : '25px',
                            height: link.path === pathName ? '29px' : '25px',
                          }}
                          alt="action"
                        />
                      </IconButton>
                      {link.Text}
                    </RouterLink>
                  </ListItem>
                ))}
              </List>
              {!matchesMobile ? (
                <List>
                  <ListItem button key="logout" onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutIcon />
                    </ListItemIcon>
                    <ListItemText primary="Log out" />
                  </ListItem>
                </List>
              ) : (
                ''
              )}
            </div>
          </DesktopDrawer>
        ) : (
          <div>
            {(['left'] as const).map((anchor) => (
              <React.Fragment key={anchor}>
                <Drawer anchor="left" open={state['left']} onClose={toggleMobileDrawer('left', false)}>
                  <div className={classes.LogoContainer}>
                    <IconButton>
                      <img
                        src={matchesMobile ? WhiteInosio : WhiteInosio}
                        style={{
                          width: matchesMobile ? '58px' : '74px',
                          height: matchesMobile ? '58px' : '74px',
                        }}
                        alt="action"
                      />
                    </IconButton>
                    <Typography id="logoName">{residentsDetails && residentsDetails?.Lease?.PropertyName.slice(0, 8)}...</Typography>
                    {matchesIpad ? (
                      <Toolbar
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          px: [1],
                        }}
                      />
                    ) : (
                      ''
                    )}
                  </div>
                  {list(anchor)}
                </Drawer>
              </React.Fragment>
            ))}
          </div>
        )}
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => (theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900]),
            flexGrow: 1,
            height: '100vh',
            overflow: 'auto',
          }}
          style={{ background: '#F1F0F5', border: 'none', boxShadow: 'none' }}
          ref={(node: Node) => {
            if (node) {
              setScrollTarget(node);
            }
          }}
        >
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Switch>
              <Route exact path={path}>
                <Redirect to={`${path}/payments`} />
              </Route>
              {/* for now, this is a safe assumption */}
              <defaultUIContext.Provider value={UIContextEnum.resUI}>
                <Route path={`${path}/payments`} component={Payments} />
                <Route path={`${path}/applications`}>
                  <Applications mainRef={mainRef} />
                </Route>
                <Route path={`${path}/profilesettings`} component={ProfileSettings} />
                {PropertyGroupDetails.MaintenanceEnabled && <Route path={`${path}/maintenance`} component={Maintenance} />}
              </defaultUIContext.Provider>
            </Switch>
            <Copyright sx={{ pt: 4 }} />
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
